import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "../Button/Button";

export type ModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  label?: string;
};

export function Modal({ isOpen, onClose, children, label = "Image preview" }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && onClose) {
        event.preventDefault();
        onClose();
      }
      if (event.key !== "Tab") return;
      const elements = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) || []).filter(element => !element.hidden && element.getAttribute("aria-hidden") !== "true");
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (!first) { event.preventDefault(); dialogRef.current?.focus(); }
      else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus();
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return createPortal(
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-overlay/[.388235]">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} className="relative rounded-lg bg-black p-4 outline-none max-[600px]:overflow-y-auto">
        {onClose && <Button variant="ghost" size="none" aria-label="Close dialog" onClick={onClose} className="absolute -right-[15px] -top-[15px] flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-black bg-white text-[21px] text-black max-[600px]:right-0 max-[600px]:top-0 max-[600px]:h-10 max-[600px]:w-10">X</Button>}
        {children}
      </div>
    </div>, document.body,
  );
}
